import { useEffect, useState } from 'react'
import Icons from '../../../Icons'
import DefaultButton from '@/components/DefaultButton/DefaultButton'
import SliderCustom from '@/components/SliderCustom'
import { useTranslation } from 'react-i18next'
import Loading from '@/components/Loading'
import SliderTabs, { SliderTabsLayout } from '@/components/SliderTabs/SliderTabs'

/** Every adjustment slider the core knows how to render. */
export type AdjustKey =
  | 'exposure'
  | 'contrast'
  | 'saturation'
  | 'vibrance'
  | 'temperature'
  | 'tint'
  | 'shadows'
  | 'highlights'
  | 'fade'
  | 'sharpness'
  | 'blur'

/** All editor sliders, in display order. */
export const ALL_ADJUST_KEYS: AdjustKey[] = [
  'exposure',
  'contrast',
  'saturation',
  'vibrance',
  'temperature',
  'tint',
  'shadows',
  'highlights',
  'fade',
  'sharpness',
  'blur',
]

interface SliderDef {
  /** Icon factory (Icons.* component). */
  icon: (size: number) => JSX.Element
  /** Translation key for the slider label. */
  labelKey: string
  /** Optional slider step. */
  step?: number
  /** [-100, 100] with a snap line at 0 */
  bipolar?: boolean
}

const SLIDER_DEFS: Record<AdjustKey, SliderDef> = {
  exposure:    { icon: (s) => <Icons.Exposure size={s} />,   labelKey: 'Exposure',    bipolar: true },
  contrast:    { icon: (s) => <Icons.Contrast size={s} />,   labelKey: 'Contrast',    bipolar: true },
  saturation:  { icon: (s) => <Icons.Saturation size={s} />, labelKey: 'Saturation',  bipolar: true },
  vibrance:    { icon: (s) => <Icons.Vibrance size={s} />,   labelKey: 'Vibrance',    bipolar: true },
  temperature: { icon: (s) => <Icons.Warmth size={s} />,     labelKey: 'Warmth',      bipolar: true },
  tint:        { icon: (s) => <Icons.Tint size={s} />,       labelKey: 'Tint',        bipolar: true },
  shadows:     { icon: (s) => <Icons.Shadow size={s} />,     labelKey: 'Shadow' },
  highlights:  { icon: (s) => <Icons.HighLight size={s} />,  labelKey: 'Highlights' },
  fade:        { icon: (s) => <Icons.Fade size={s} />,       labelKey: 'Fade' },
  sharpness:   { icon: (s) => <Icons.Sharpness size={s} />,  labelKey: 'Sharpness' },
  blur:        { icon: (s) => <Icons.Blur size={s} />,       labelKey: 'Blur', step: 10 },
}

const SLIDER_LIST_VALUES = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
const SLIDER_LIST_VALUES_BIPOLAR = [-100, -80, -60, -40, -20, 0, 20, 40, 60, 80, 100]

/**
 * Pure adjustment-sliders UI, driven entirely by callback props — mirrors the
 * Shadow core so the same component serves the single-image editor (AdjustImage)
 * and Batch Studio (AdjustBatch). It owns only the slider UI state; how values
 * are read, applied and committed is delegated to the adapter via props.
 */
function Adjust({
  applyAdjustments,
  updateOptions,
  updateEffect,
  calcMaxForKey,
  calcMinForKey,
  defaultForKey,
  target,
  waitUntilReady,
  sliders = ALL_ADJUST_KEYS,
  canReset = false,
  onAutoAdjust,
  autoAdjustLabelKey = 'Auto Adjust',
  onResetStateChange,
  autoAdjustLayout = 'prominent',
  sliderLayout = 'stacked',
}: {
  applyAdjustments: (isReset: boolean, adjustments: Record<string, number>) => Promise<void> | void
  /** Reads current values from the adapter; returns `null` per key when mixed/absent. */
  updateOptions: (target: any) => Record<string, number | null>
  updateEffect: (final?: boolean) => void
  calcMaxForKey: (key: string) => number
  calcMinForKey: (key: string) => number
  defaultForKey: (key: string) => number
  target: any
  waitUntilReady: (onReady: () => void, signal?: AbortSignal) => void
  /** Ordered subset of sliders to render. Defaults to all editor sliders. */
  sliders?: AdjustKey[]
  /** When true, Reset fires even if displayed values are already at default. */
  canReset?: boolean
  /** Optional callback to auto-compute values and sync the panel. */
  onAutoAdjust?: () => Promise<Record<string, number> | null>
  /** Translation key for the auto-adjust action. */
  autoAdjustLabelKey?: string
  /** Reports the current reset handler + whether it's actionable, so a host (e.g. the inspector header) can render its own Reset button. */
  onResetStateChange?: (reset: () => void, isResetEnabled: boolean) => void
  /** 'compact' puts a standard-height, full-width auto-adjust button after the sliders (phones). */
  autoAdjustLayout?: 'prominent' | 'compact'
  /** Presentation only: one selected slider with the same values and callbacks. */
  sliderLayout?: 'stacked' | 'tabbed'
}) {
  const { t } = useTranslation()

  const buildDefaults = (): Record<string, number> =>
    Object.fromEntries(sliders.map((key) => [key, defaultForKey(key)]))

  const [values, setValues] = useState<Record<string, number>>(buildDefaults)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isAutoAdjusting, setIsAutoAdjusting] = useState(false)

  const seedFromTarget = () => {
    const incoming = updateOptions(target) || {}
    const next: Record<string, number> = {}
    for (const key of sliders) {
      const value = incoming[key]
      next[key] = value ?? defaultForKey(key)
    }
    setValues(next)
  }

  useEffect(() => {
    if (!target) return
    // Invalidate immediately on target switch — before this, isInitialized/values
    // still describe the previously selected target.
    setIsInitialized(false)
    const abortController = new AbortController()
    waitUntilReady(() => {
      if (abortController.signal.aborted) return
      setIsInitialized(true)
      seedFromTarget()
    }, abortController.signal)

    return () => {
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target])

  const applyEffect = async (next: Record<string, number>, isReset: boolean) => {
    await applyAdjustments(isReset, next)
    updateEffect(isReset)
  }

  const onSliderChange = (key: AdjustKey, logicValue: number) => {
    const next = { ...values, [key]: logicValue }
    setValues(next)
    applyEffect(next, false)
  }

  const reset = () => {
    const defaults = buildDefaults()
    const alreadyDefault = sliders.every((key) => values[key] === defaults[key])
    if (alreadyDefault && !canReset) {
      return
    }
    setValues(defaults)
    applyEffect(defaults, true)
  }

  useEffect(() => {
    if (!isInitialized) {
      // Target is (re)initializing — invalidate any previously reported handler so
      // the header button can't fire against the target we're switching away from.
      onResetStateChange?.(() => {}, false)
      return
    }
    const defaults = buildDefaults()
    const isResetEnabled = canReset || !sliders.every((key) => values[key] === defaults[key])
    onResetStateChange?.(reset, isResetEnabled)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, isInitialized, values, canReset])

  const handleAutoAdjust = async () => {
    if (!onAutoAdjust || isAutoAdjusting) {
      return
    }

    setIsAutoAdjusting(true)
    try {
      const nextValues = await onAutoAdjust()
      if (nextValues === null) {
        seedFromTarget()
        return
      }

      if (nextValues) {
        setValues((prevValues) => ({ ...prevValues, ...nextValues }))
      }
    } catch (error) {
      console.error('Auto adjust failed', error)
    } finally {
      setIsAutoAdjusting(false)
    }
  }

  const renderSlider = (key: AdjustKey, index: number) => {
    const def = SLIDER_DEFS[key]
    return (
      <SliderCustom
        key={key}
        icon={sliderLayout === 'tabbed' ? undefined : def.icon(24)}
        name={t(def.labelKey)}
        minValue={def.bipolar ? -100 : 0}
        maxValue={100}
        step={def.step}
        logicMaximum={calcMaxForKey(key)}
        logicMinimum={calcMinForKey(key)}
        listValues={def.bipolar ? SLIDER_LIST_VALUES_BIPOLAR : SLIDER_LIST_VALUES}
        snapValue={def.bipolar ? 0 : undefined}
        fillMode={def.bipolar ? 'center' : undefined}
        onChangeValue={(_uiValue, logicValue) => onSliderChange(key, logicValue)}
        value={values[key]}
        onFinalChangeValue={() => updateEffect(true)}
        style={index === 0 ? { marginTop: 0 } : undefined}
      />
    )
  }

  const autoAdjustButton = (
    <DefaultButton
      type="secondary-black"
      size={sliderLayout === 'tabbed' || autoAdjustLayout === 'prominent' ? 'large' : 'standard'}
      icon="iconLeft"
      iconComponent={<Icons.MagicWand />}
      label={isAutoAdjusting ? `${t(autoAdjustLabelKey)}...` : t(autoAdjustLabelKey)}
      disabled={isAutoAdjusting}
      fullWidth
      onClick={handleAutoAdjust}
    />
  )

  return (
    <>
      {isInitialized ? (
        sliderLayout === 'tabbed' ? (
          <SliderTabsLayout>
            {onAutoAdjust && autoAdjustButton}
            <SliderTabs
              tabs={sliders.map((key) => ({
                key,
                label: t(SLIDER_DEFS[key].labelKey),
                icon: SLIDER_DEFS[key].icon(24),
                content: renderSlider(key, 0),
              }))}
            />
          </SliderTabsLayout>
        ) : <>
          {onAutoAdjust && autoAdjustLayout === 'prominent' && (
            <div className="mb-50">
              {autoAdjustButton}
            </div>
          )}
          {sliders.map(renderSlider)}
          {onAutoAdjust && autoAdjustLayout === 'compact' && (
            <div style={{ marginTop: '20px' }}>
              {autoAdjustButton}
            </div>
          )}
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Loading />
        </div>
      )}
    </>
  )
}

export default Adjust
